// VerifyEmail.jsx - Handles email verification link clicks
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { LeafIcon } from '../components/ui/Icons';

const VerifyEmail = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
    const [message, setMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Kein Token gefunden.');
            return;
        }

        const verify = async () => {
            try {
                const response = await fetch(`${API_URL}/api/verify-email/${token}/`, {
                    method: 'GET',
                });
                const contentType = response.headers.get('content-type');
                const data = contentType?.includes('application/json')
                    ? await response.json()
                    : { error: 'Unbekannter Fehler vom Server.' };

                if (response.ok) {
                    setStatus('success');
                    setMessage(data.message || 'E-Mail erfolgreich bestätigt!');
                } else {
                    setStatus('error');
                    setMessage(data.error || 'Der Link ist ungültig oder abgelaufen.');
                }
            } catch (err) {
                setStatus('error');
                setMessage('Netzwerkfehler. Bitte versuche es erneut.');
            }
        };

        verify();
    }, [token]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-plantlix-50 via-plantlix-50 to-plantlix-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center">
                <div className="card p-10 bg-white/70 dark:bg-gray-900/60 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 shadow-2xl rounded-3xl">

                    <div className="mb-6 inline-flex items-center justify-center p-3 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-2xl">
                        <LeafIcon color="#16a34a" className="w-10 h-10" />
                    </div>

                    {status === 'loading' && (
                        <>
                            <div className="animate-spin rounded-full h-12 w-12 border-4 border-plantlix-200 border-t-plantlix-600 mx-auto mb-6"></div>
                            <h2 className="text-2xl font-extrabold text-gray-800 dark:text-gray-100">
                                E-Mail wird bestätigt…
                            </h2>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Bitte einen Moment warten.</p>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="text-6xl mb-4">🎉</div>
                            <h2 className="text-2xl font-extrabold text-gray-800 dark:text-gray-100">
                                Account aktiviert!
                            </h2>
                            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                Deine E-Mail wurde bestätigt. Du kannst dich jetzt anmelden.
                            </p>
                            <button
                                onClick={() => navigate('/auth')}
                                className="mt-8 w-full py-4 px-4 rounded-xl shadow-lg text-base font-bold text-white bg-plantlix-600 hover:bg-plantlix-700 transition-all active:scale-95"
                            >
                                Jetzt anmelden →
                            </button>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="text-6xl mb-4">⚠️</div>
                            <h2 className="text-2xl font-extrabold text-gray-800 dark:text-gray-100">
                                Bestätigung fehlgeschlagen
                            </h2>
                            <p className="mt-3 text-sm text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl p-3">
                                {message}
                            </p>
                            <p className="mt-3 text-xs text-gray-400">
                                Der Link ist möglicherweise bereits verwendet worden oder abgelaufen (24h). Fordere über den Login-Bereich einen neuen Link an.
                            </p>
                            <button
                                onClick={() => navigate('/auth')}
                                className="mt-6 w-full py-4 px-4 rounded-xl shadow-lg text-base font-bold text-plantlix-700 bg-plantlix-50 hover:bg-plantlix-100 dark:bg-plantlix-900/30 dark:text-plantlix-400 transition-all active:scale-95"
                            >
                                Zum Login
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail;
